import React, { useState, useEffect } from 'react'
import { useFileUpload, useFileDelete } from '../../lib/api/hooks/file-upload'
import { showToast } from '../../lib/toast'

interface ImageUploadFieldProps {
  label: string
  value?: string | null
  preview?: string
  onChange: (url: string | null) => void
  onAltTextChange?: (altText: string) => void
  required?: boolean
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  label,
  value,
  preview,
  onChange,
  onAltTextChange,
  required = false
}) => {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [localPreview, setLocalPreview] = useState<string>(preview || '')
  const [showDropdown, setShowDropdown] = useState(false)
  const [showAltTextInput, setShowAltTextInput] = useState(false)
  const [altText, setAltText] = useState('')
  const { mutateAsync: uploadFile } = useFileUpload()
  const { mutateAsync: deleteFile } = useFileDelete()

  // Sync localPreview with preview prop (for edit mode when data loads)
  useEffect(() => {
    if (preview) {
      setLocalPreview(preview)
    }
  }, [preview])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      showToast.error('Please select an image file')
      return
    }

    const maxSize = 5 * 1024 * 1024 // 5MB
    if (file.size > maxSize) {
      showToast.error('Image size should be less than 5MB')
      return
    }

    // Show local preview immediately
    const reader = new FileReader()
    reader.onloadend = () => {
      setLocalPreview(reader.result as string)
    }
    reader.readAsDataURL(file)

    // Upload to server
    try {
      setUploading(true)
      setUploadProgress(0)

      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90))
      }, 200)

      const response = await uploadFile({ file, category: 'IMAGE' })

      clearInterval(progressInterval)
      setUploadProgress(100)

      if (response.success && response.data?.fileUrl) {
        showToast.success('Image uploaded successfully!')
        onChange(response.data.fileUrl)
        setLocalPreview(response.data.fileUrl)
      } else {
        throw new Error('Upload failed: No file URL returned')
      }
    } catch (error) {
      console.error('Upload error:', error)
      showToast.error(error instanceof Error ? error.message : 'Failed to upload image')
      setLocalPreview('')
    } finally {
      setUploading(false)
      setTimeout(() => setUploadProgress(0), 1000)
    }
  }

  const handleDeleteImage = async () => {
    if (!value && !localPreview) return

    try {
      // Delete from server if there's a server URL
      if (value) {
        await deleteFile(value)
      }

      setLocalPreview('')
      setShowAltTextInput(false)
      setAltText('')
      onChange(null)
      if (onAltTextChange) onAltTextChange('')
      setShowDropdown(false)
      showToast.success('Image deleted successfully!')
    } catch (error) {
      console.error('Delete error:', error)
      showToast.error('Failed to delete image')
    }
  }

  const handleAltTextSave = () => {
    if (onAltTextChange) {
      onAltTextChange(altText)
    }
    setShowDropdown(false)
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {localPreview ? (
        <div className="relative w-full">
          {/* Banner-style image */}
          <img
            src={localPreview}
            alt="Cover"
            className="w-full h-64 object-cover rounded-lg"
          />

          {/* Three-dot menu button at top right */}
          <div className="absolute top-3 right-3">
            <button
              type="button"
              onClick={() => setShowDropdown(!showDropdown)}
              className="bg-gray-100 rounded-full p-2  hover:bg-gray-100 transition-colors opacity-75"
            >
              <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                <button
                  type="button"
                  onClick={() => {
                    setShowAltTextInput(!showAltTextInput)
                    setShowDropdown(false)
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Add Alt Text
                </button>
                <button
                  type="button"
                  onClick={handleDeleteImage}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                >
                  Delete Image
                </button>
              </div>
            )}
          </div>

          {/* Alt text input below image */}
          {showAltTextInput && (
            <div className="mt-3">
              <input
                type="text"
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                onBlur={handleAltTextSave}
                placeholder="Enter alt text for accessibility"
                className="w-full px-3 py-2 border-none outline-none focus:outline-none focus:ring-0 bg-gray-50 rounded-lg text-sm"
              />
            </div>
          )}
        </div>
      ) : (
        <div>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
            className="hidden"
            id="cover-image-upload"
            required={required && !value}
          />
          <label
            htmlFor="cover-image-upload"
            className={`inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors ${
              uploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-medium">Add cover image</span>
          </label>
        </div>
      )}

      {uploading && (
        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
          <div
            className="bg-wine-100 h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      )}
    </div>
  )
}

export default ImageUploadField
